import axios from "axios";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Box, Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getSuppliesFileringOptions,
  getClearSupplyModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import AddOrEditSupply from "./components/AddOrEditSupply";
import EntityPrinter from "../../components/common/EntityPrinter";
import ScrollablePageTab from "../dynamicForm/components/ScrollablePageTab";
import { TabContext, TabPanel } from "@mui/lab";
import SuppliesOnWarehouse from "./components/SuppliesOnWarehouse";

export default function MeasurementUnitList() {
  const {
    supplyModel,
    setSupplyModel,
    supplyData,
    SUPPLY_API,
    config,
    pageIndexSupply,
    setPageIndexSupply,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    setBasicFilterOptions,
    basicFilterData,
    WAREHOUSE_ENDPOINT,
  } = useGlobalContext();
  const { id } = useParams();
  const [supplyDataFiltered, setSupplyDataFiltered] = useState(supplyData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReportPrinterModal, setShowReportPrinterModal] = useState(false);
  const [isLoadingPrinter, setIsLoadingPrinter] = useState(false);
  const [selectedTab, setSelectedTab] = useState("0");
  const [warehouses, setWarehouses] = useState(undefined);
  const [warehousePages, setWarehousePages] = useState([
    {
      Id: "0",
      Name: localize.main_warehouse,
    },
  ]);

  const changeTab = (value) => {
    value >= 0 && setSelectedTab(value.toString());
  };

  const getWarehouses = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WAREHOUSE_ENDPOINT}`, requestConfig);

    if (data?.data?.Data && data?.data?.Data.length > 0) {
      setWarehousePages([
        warehousePages[0],
        ...data?.data?.Data.map((warehouse) => ({
          Id: warehouse.Id,
          Name: warehouse.Name,
        })),
      ]);

      setWarehouses(data?.data?.Data);
    }
  };

  useEffect(() => {
    setBasicFilterOptions(getSuppliesFileringOptions());
    getWarehouses();
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const supplyDataObj = supplyData.Data.find(
        (supplyObj) => supplyObj.Id === Number(id)
      );
      setSupplyModel(supplyDataObj);
    }
  }, [id]);

  useEffect(() => {
    setSupplyDataFiltered(supplyData.Data);
  }, [supplyData]);

  const validateOperationResult = (result, isDelete) => {
    setSupplyModel(getClearSupplyModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([SUPPLY_API, config]);
    if (isDelete) {
      if (pageIndexSupply !== 0 && supplyData.Data.length === 1) {
        setPageIndexSupply(pageIndexSupply - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${SUPPLY_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const finalData = {
      ...data,
      Tags: JSON.stringify(data.Tags),
    };

    const updateResult = await updateEntity(
      `${`${SUPPLY_API}/${finalData.Id}`}`,
      finalData,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const finalData = {
      ...data,
      Tags: JSON.stringify(data.Tags),
    };

    const updateResult = await createEntity(
      `${`${SUPPLY_API}`}`,
      finalData,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !supplyModel?.Name ||
    supplyModel?.Name.replaceAll(" ", "").length <= 0 ||
    !supplyModel.Code ||
    supplyModel?.Code.replaceAll(" ", "").length <= 0 ||
    !supplyModel?.Unit;

  const preparePrinting = async () => {
    setIsLoadingPrinter(true);

    try {
      const data = await axios.get(SUPPLY_API, {
        ...config,
        headers: {
          ...config.headers,
          pagination: -1,
          filtering: JSON.stringify(basicFilterData),
        },
      });

      if (data.data.Ok && data.data.Data) {
        setShowReportPrinterModal(true);
        setIsLoadingPrinter(false);
        return;
      }
    } catch (error) {
      console.error("Error loading supplies: ", error);
    }

    setErrorMessage(localize.error_unexpected);
    setShowErrorModal(true);
    setIsLoadingPrinter(false);
  };

  const getColumnTitles = () => {
    return [
      localize.code,
      localize.name,
      localize.quantity,
      localize.measurement_unit,
      localize.provider,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showReportPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowReportPrinterModal(false)}
          title={localize.print}
          report_title={localize.supplies}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={SUPPLY_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="supply"
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_supply}
          onClickToSubmit={() => {
            handleUpdateRequest({
              ...supplyModel,
              UnitId: supplyModel?.Unit?.Id,
            });
            setShowEditModal(false);
            setSupplyModel(getClearSupplyModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setSupplyModel(getClearSupplyModel());
          }}
          Component={
            <AddOrEditSupply model={supplyModel} setModel={setSupplyModel} />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.supplies}
            subtitle={localize.supplies_description}
          />

          <SearcherAndAdd
            isPrintLoading={isLoadingPrinter}
            printButtonDisabled={
              isLoadingPrinter || supplyData?.Data?.length <= 0
            }
            printButtonMethod={() => {
              preparePrinting();
            }}
            entityName="supply_filter"
            handleAddBtn={() => {
              setSupplyModel(getClearSupplyModel());
              setShowAddModal(true);
            }}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_supply}
                onClickToSubmit={() => {
                  handleCreateRequest(supplyModel);
                  setShowAddModal(false);
                  setSupplyModel(getClearSupplyModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setSupplyModel(getClearSupplyModel());
                }}
                Component={
                  <AddOrEditSupply
                    model={supplyModel}
                    setModel={setSupplyModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={supplyModel.Name}
          entityType={localize.supply}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setSupplyModel(getClearSupplyModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(supplyModel.Id);
            setShowDeleteModal(false);
            setSupplyModel(getClearSupplyModel());
          }}
        />
      )}

      <TabContext value={selectedTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <ScrollablePageTab
            selectedPage={parseInt(selectedTab)}
            setSelectedPage={changeTab}
            noMarginTop
            dynamicFormPages={warehousePages}
          />
        </Box>
        <TabPanel value="0" index={0} sx={{ padding: "0px !important;" }}>
          <TableComponent
            pagination={
              <PagePagination
                pageIndex={pageIndexSupply}
                setPageIndex={setPageIndexSupply}
                dataArray={supplyData}
              />
            }
            firstColumnTitle={localize.actions}
            columnTitles={getColumnTitles()}
            results={
              supplyData.Data?.length > 0
                ? supplyDataFiltered.map((item) => ({
                    key: item.Id,
                    data: [
                      <div>
                        <ActionButtons
                          onClickForEditButton={() => {
                            setSupplyModel({
                              ...item,
                              ProviderId: item?.Provider?.Id,
                            });
                            setShowEditModal(true);
                          }}
                          onclickForDeleteButton={() => {
                            setSupplyModel(item);
                            setShowDeleteModal(true);
                            toggleVisibleModalConfirm();
                          }}
                        />
                      </div>,
                      <ValueOrNoDataLabel data={item.Code} />,
                      <ValueOrNoDataLabel data={item.Name} />,
                      item.Quantity,
                      <ValueOrNoDataLabel
                        data={
                          item.Unit?.Symbol && item.Unit?.Symbol !== ""
                            ? `${item.Unit?.Name} (${item.Unit?.Symbol})`
                            : `${item.Unit?.Name}`
                        }
                      />,
                      <ValueOrNoDataLabel data={item.Provider?.Name} />,
                      new Date(item.ModificationDate).toLocaleString(
                        undefined,
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        }
                      ),
                      item.CreatedBy,
                    ],
                  }))
                : [generateNoDataValues(8)]
            }
          />
        </TabPanel>
        {warehouses &&
          warehouses.length > 0 &&
          warehouses.map((warehouse, index) => (
            <TabPanel
              key={warehouse.Id}
              value={index + 1 + ""}
              index={index + 1}
              sx={{ padding: "0px !important;" }}
            >
              <SuppliesOnWarehouse
                warehouse={warehouse}
                filtering={basicFilterData}
              />
            </TabPanel>
          ))}
      </TabContext>
    </div>
  );
}
